<template>
  <v-container fluid class="pa-4">

    <!-- Header -->
    <div class="d-flex align-center mb-3">
      <h1 class="text-h4 mb-0">
        <v-icon icon="mdi-calendar-account" class="me-2" color="primary" />
        {{ $t('student.schedule.title') }}
      </h1>
      <v-spacer />
      <v-btn icon="mdi-refresh" size="small" variant="text" :loading="loading" @click="loadSchedule" />
    </div>

    <!-- No instructor warning -->
    <v-alert
      v-if="!currentInstructor && !loading"
      type="warning"
      variant="tonal"
      class="mb-4"
    >
      <v-alert-title>{{ $t('student.schedule.noInstructor') }}</v-alert-title>
      {{ $t('student.schedule.noInstructorDesc') }}
      <v-btn
        variant="text"
        color="warning"
        class="ms-2"
        @click="$router.push('/student/instructors')"
      >
        {{ $t('student.instructors.title') }}
      </v-btn>
    </v-alert>

    <!-- Instructor info card (compact) -->
    <v-card v-if="currentInstructor" class="mb-3" variant="tonal" color="primary" elevation="0">
      <v-card-text class="d-flex align-center pa-2 ga-3">
        <v-avatar size="44" class="flex-shrink-0">
          <v-img v-if="currentInstructor.photo" :src="photoUrl(currentInstructor.photo)" cover />
          <v-icon v-else>mdi-account-tie</v-icon>
        </v-avatar>
        <div class="flex-grow-1 min-w-0">
          <div class="text-subtitle-2 font-weight-bold text-truncate">{{ currentInstructor.fio }}</div>
          <div class="text-caption text-medium-emphasis">
            <v-icon size="x-small">mdi-car</v-icon>
            {{ currentInstructor.carModel || '—' }}
          </div>
        </div>
        <div class="d-flex align-center ga-1 flex-shrink-0">
          <v-rating
            :model-value="currentInstructor.rating"
            readonly
            color="amber"
            size="x-small"
            density="compact"
          />
          <span class="text-caption me-1">{{ currentInstructor.rating?.toFixed(1) }}</span>
        </div>
        <!-- Phone call button -->
        <v-tooltip v-if="currentInstructor.phone" :text="currentInstructor.phone" location="top">
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              :href="`tel:${currentInstructor.phone}`"
              icon="mdi-phone"
              size="small"
              color="success"
              variant="tonal"
              class="flex-shrink-0"
            />
          </template>
        </v-tooltip>
      </v-card-text>
    </v-card>

    <!-- Navigation bar -->
    <v-card class="mb-3" variant="tonal" elevation="0">
      <v-card-text class="d-flex align-center ga-1 flex-wrap py-2 px-3">
        <!-- Week back -->
        <v-btn icon="mdi-chevron-double-left" size="small" variant="text" @click="shiftWeek(-1)" />
        <!-- Day back -->
        <v-btn icon="mdi-chevron-left"        size="small" variant="text" @click="shiftDays(-1)" />

        <div class="font-weight-bold text-body-2 mx-1">{{ weekRangeLabel }}</div>

        <!-- Day forward -->
        <v-btn icon="mdi-chevron-right"        size="small" variant="text" @click="shiftDays(1)" />
        <!-- Week forward -->
        <v-btn icon="mdi-chevron-double-right" size="small" variant="text" @click="shiftWeek(1)" />

        <v-btn size="x-small" variant="tonal" color="primary" class="ms-1" @click="goToToday">
          {{ $t('common.today') }}
        </v-btn>

        <v-spacer />

        <!-- Legend -->
        <div class="d-flex align-center ga-3 text-caption text-medium-emphasis flex-wrap">
          <span>
            <v-icon size="x-small" color="primary">mdi-circle</v-icon>
            {{ $t('schedule.status.SCHEDULED') }}
          </span>
          <span>
            <v-icon size="x-small" color="success">mdi-circle</v-icon>
            {{ $t('schedule.status.COMPLETED') }}
          </span>
          <span>
            <v-icon size="x-small" color="warning">mdi-circle</v-icon>
            {{ $t('schedule.status.RESCHEDULED') }}
          </span>
        </div>
      </v-card-text>
    </v-card>

    <!-- Loading spinner -->
    <div v-if="loading" class="text-center pa-12">
      <v-progress-circular indeterminate color="primary" size="48" />
    </div>

    <!-- Schedule grid -->
    <v-card v-else elevation="2" class="schedule-card">
      <v-card-text class="pa-0">
        <div class="sg-grid" :style="gridStyle">

          <!-- Corner cell -->
          <div class="sg-cell sg-corner">
            {{ $t('instructor.schedule.timeDay') }}
          </div>

          <!-- Date column headers -->
          <div
            v-for="col in weekDays"
            :key="col.iso"
            class="sg-cell sg-col-header"
            :class="{ 'sg-today-col': col.iso === todayIso }"
          >
            <div class="sg-date-num">{{ col.dayNum }}</div>
            <div class="sg-date-month">{{ col.monthLabel }}</div>
            <div class="sg-date-dow">{{ col.dowLabel }}</div>
          </div>

          <!-- Hour rows -->
          <template v-for="hour in HOUR_ROWS" :key="hour">
            <div class="sg-cell sg-row-header">{{ hour }}</div>

            <div
              v-for="col in weekDays"
              :key="col.iso + hour"
              class="sg-cell sg-data-cell"
              :class="dataCellClass(col.iso, col, hour)"
              @click="onCellClick(col.iso, hour)"
            >
              <v-icon
                v-if="getCellLesson(col.iso, hour)"
                :color="statusIconColor(getCellLesson(col.iso, hour).status)"
                size="9"
                class="sg-dot"
              >mdi-circle</v-icon>
            </div>
          </template>

        </div>
      </v-card-text>

      <!-- Empty state below grid when no lessons at all -->
      <v-card-text v-if="!allLessons.length" class="text-center py-8 text-medium-emphasis">
        <v-icon size="56" color="grey-lighten-1">mdi-calendar-blank-outline</v-icon>
        <p class="mt-2 text-body-2">{{ $t('student.schedule.noLessons') }}</p>
      </v-card-text>
    </v-card>

    <!-- Hint text -->
    <div v-if="!loading && allLessons.length" class="text-caption text-medium-emphasis mt-2 text-center">
      {{ $t('student.schedule.clickHint') }}
    </div>

    <!-- Stats row (below grid) -->
    <v-row class="mt-3" dense>
      <v-col cols="6" sm="3">
        <v-card color="success" variant="tonal" elevation="0">
          <v-card-text class="text-center pa-2">
            <div class="text-h5 font-weight-bold">{{ stats.completed }}</div>
            <div class="text-caption">{{ $t('student.schedule.completedLessons') }}</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="6" sm="3">
        <v-card color="primary" variant="tonal" elevation="0">
          <v-card-text class="text-center pa-2">
            <div class="text-h5 font-weight-bold">{{ stats.scheduled }}</div>
            <div class="text-caption">{{ $t('student.schedule.scheduledLessons') }}</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="6" sm="3">
        <v-card color="warning" variant="tonal" elevation="0">
          <v-card-text class="text-center pa-2">
            <div class="text-h5 font-weight-bold">{{ stats.upcoming }}</div>
            <div class="text-caption">{{ $t('student.schedule.upcomingLessons') }}</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="6" sm="3">
        <v-card color="info" variant="tonal" elevation="0">
          <v-card-text class="text-center pa-2">
            <div class="text-h5 font-weight-bold">{{ stats.totalHours }}</div>
            <div class="text-caption">{{ $t('student.schedule.totalHours') }}</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Lesson details dialog -->
    <v-dialog v-model="detailsDialog.visible" max-width="440">
      <v-card v-if="detailsDialog.lesson">
        <v-card-title class="d-flex align-center pb-2 pt-3 px-4">
          <v-icon
            size="small"
            class="me-2"
            :color="statusColor(detailsDialog.lesson.status)"
          >mdi-calendar-check</v-icon>
          {{ $t('student.schedule.lessonDetails') }}
          <v-spacer />
          <v-btn icon="mdi-close" size="small" variant="text" @click="detailsDialog.visible = false" />
        </v-card-title>
        <v-divider />

        <v-card-text class="pa-4">

          <!-- Date -->
          <div class="detail-row">
            <v-icon size="18" class="detail-icon" color="primary">mdi-calendar</v-icon>
            <div>
              <div class="text-caption text-medium-emphasis mb-0">{{ $t('common.date') }}</div>
              <div class="text-body-2 font-weight-medium">{{ formatDate(detailsDialog.lesson.date) }}</div>
            </div>
          </div>

          <!-- Time -->
          <div class="detail-row">
            <v-icon size="18" class="detail-icon" color="primary">mdi-clock-outline</v-icon>
            <div>
              <div class="text-caption text-medium-emphasis mb-0">{{ $t('common.time') }}</div>
              <div class="text-body-2 font-weight-medium">{{ detailsDialog.lesson.timeSlot }}</div>
            </div>
          </div>

          <!-- Instructor -->
          <div class="detail-row">
            <v-icon size="18" class="detail-icon" color="primary">mdi-account-tie</v-icon>
            <div class="min-w-0 flex-grow-1">
              <div class="text-caption text-medium-emphasis mb-0">{{ $t('common.instructor') }}</div>
              <div class="text-body-2 font-weight-medium">
                {{ detailsDialog.lesson.instructor?.fio || '—' }}
              </div>
              <div v-if="detailsDialog.lesson.instructor?.carModel" class="text-caption text-medium-emphasis">
                <v-icon size="x-small">mdi-car</v-icon>
                {{ detailsDialog.lesson.instructor.carModel }}
              </div>
            </div>
          </div>

          <!-- Address / Location -->
          <div class="detail-row">
            <v-icon size="18" class="detail-icon" color="warning">mdi-map-marker</v-icon>
            <div class="min-w-0 flex-grow-1">
              <div class="text-caption text-medium-emphasis mb-0">{{ $t('student.schedule.addressLabel') }}</div>
              <div class="text-body-2 font-weight-medium">
                {{ detailsDialog.lesson.address?.address || $t('student.schedule.noAddress') }}
              </div>
              <div
                v-if="detailsDialog.lesson.address?.name"
                class="text-caption text-medium-emphasis"
              >{{ detailsDialog.lesson.address.name }}</div>
            </div>
          </div>

          <!-- Status -->
          <div class="detail-row">
            <v-icon
              size="18"
              class="detail-icon"
              :color="statusColor(detailsDialog.lesson.status)"
            >mdi-information-outline</v-icon>
            <div>
              <div class="text-caption text-medium-emphasis mb-1">{{ $t('common.status') }}</div>
              <v-chip
                size="small"
                :color="statusColor(detailsDialog.lesson.status)"
                variant="tonal"
              >
                {{ statusLabel(detailsDialog.lesson.status) }}
              </v-chip>
            </div>
          </div>

          <!-- Notes -->
          <div v-if="detailsDialog.lesson.notes" class="detail-row">
            <v-icon size="18" class="detail-icon" color="grey">mdi-note-text-outline</v-icon>
            <div class="min-w-0 flex-grow-1">
              <div class="text-caption text-medium-emphasis mb-0">{{ $t('common.notes') }}</div>
              <div class="text-body-2">{{ detailsDialog.lesson.notes }}</div>
            </div>
          </div>

        </v-card-text>

        <v-divider />
        <v-card-actions class="pa-3">
          <v-spacer />
          <v-btn variant="text" @click="detailsDialog.visible = false">
            {{ $t('common.close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import api from '@/utils/api'

const router = useRouter()
const { t } = useI18n()
const authStore = useAuthStore()

// ─── Constants ────────────────────────────────────────────────────────────────
const VISIBLE_DAYS = 7
const HOUR_ROWS = [
  '08:00', '09:00', '10:00', '11:00', '12:00', '13:00',
  '14:00', '15:00', '16:00', '17:00', '18:00', '19:00'
]
const KZ_MONTHS_SHORT = ['қаң', 'ақп', 'нау', 'сәу', 'мам', 'мау', 'шіл', 'там', 'қыр', 'қаз', 'қар', 'жел']
const KZ_DAYS_SHORT   = ['Жс', 'Дс', 'Сс', 'Ср', 'Бс', 'Жм', 'Сн']

// ─── State ────────────────────────────────────────────────────────────────────
const loading         = ref(false)
const allLessons      = ref([])   // full list (no date filter) — for stats
const currentInstructor = ref(null)
const dateOffset      = ref(0)    // days from today

const detailsDialog   = ref({ visible: false, lesson: null })

// ─── Computed ─────────────────────────────────────────────────────────────────
const todayIso = computed(() => toLocalIso(new Date()))

const weekDays = computed(() => {
  const cols = []
  const base = new Date()
  base.setHours(0, 0, 0, 0)
  for (let i = 0; i < VISIBLE_DAYS; i++) {
    const d = new Date(base)
    d.setDate(d.getDate() + dateOffset.value + i)
    cols.push({
      iso:        toLocalIso(d),
      dayNum:     d.getDate(),
      monthLabel: KZ_MONTHS_SHORT[d.getMonth()],
      dowLabel:   KZ_DAYS_SHORT[d.getDay()],
      dayIdx:     d.getDay(),
    })
  }
  return cols
})

const weekRangeLabel = computed(() => {
  if (!weekDays.value.length) return ''
  const first = weekDays.value[0]
  const last  = weekDays.value[weekDays.value.length - 1]
  return `${first.dayNum} ${first.monthLabel} — ${last.dayNum} ${last.monthLabel}`
})

const gridStyle = computed(() => ({
  gridTemplateColumns: `60px repeat(${VISIBLE_DAYS}, minmax(44px, 1fr))`
}))

/** Lessons visible in the current 7-day window */
const visibleIsos = computed(() => new Set(weekDays.value.map(c => c.iso)))

const stats = computed(() => {
  const now = new Date()
  const completed  = allLessons.value.filter(l => l.status === 'COMPLETED').length
  const scheduled  = allLessons.value.filter(l => l.status === 'SCHEDULED').length
  const upcoming   = allLessons.value.filter(l =>
    l.status === 'SCHEDULED' && new Date(l.date) >= now
  ).length
  const totalHours = completed  // 1 slot = 1 hour
  return { completed, scheduled, upcoming, totalHours }
})

// ─── Helpers ──────────────────────────────────────────────────────────────────
const toLocalIso = (d) => {
  const yyyy = d.getFullYear()
  const mm   = String(d.getMonth() + 1).padStart(2, '0')
  const dd   = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

const photoUrl = (filename) => {
  if (!filename) return ''
  if (/^https?:\/\//i.test(filename)) return filename
  return `/images/instructors/${filename}`
}

const formatDate = (date) =>
  new Date(date).toLocaleDateString('kk-KZ', {
    year: 'numeric', month: 'long', day: 'numeric'
  })

const statusColor = (status) =>
  ({ SCHEDULED: 'primary', COMPLETED: 'success', CANCELLED: 'error', RESCHEDULED: 'warning' }[status] || 'grey')

const statusIconColor = (status) =>
  ({ SCHEDULED: 'primary', COMPLETED: 'success', CANCELLED: 'error', RESCHEDULED: 'warning' }[status] || 'grey')

const statusLabel = (status) =>
  ({
    SCHEDULED:   t('schedule.status.SCHEDULED'),
    COMPLETED:   t('schedule.status.COMPLETED'),
    CANCELLED:   t('schedule.status.CANCELLED'),
    RESCHEDULED: t('schedule.status.RESCHEDULED'),
  }[status] || status)

// ─── Grid helpers ─────────────────────────────────────────────────────────────
const getCellLesson = (iso, hour) =>
  allLessons.value.find(l => {
    if (l.status === 'CANCELLED') return false
    if (toLocalIso(new Date(l.date)) !== iso) return false
    return l.timeSlot && l.timeSlot.startsWith(hour)
  }) ?? null

const dataCellClass = (iso, col, hour) => {
  const lesson = getCellLesson(iso, hour)
  const isToday = iso === todayIso.value
  if (!lesson) {
    return isToday ? 'cell-today-empty' : 'cell-empty'
  }
  const s = lesson.status.toLowerCase()
  return isToday ? `cell-today-${s}` : `cell-${s}`
}

// ─── Navigation ───────────────────────────────────────────────────────────────
const shiftDays = (delta) => { dateOffset.value += delta }
const shiftWeek = (delta) => { dateOffset.value += delta * 7 }
const goToToday = () => { dateOffset.value = 0 }

// ─── Cell click ───────────────────────────────────────────────────────────────
const onCellClick = (iso, hour) => {
  const lesson = getCellLesson(iso, hour)
  if (lesson) {
    detailsDialog.value = { visible: true, lesson }
  }
}

// ─── API ──────────────────────────────────────────────────────────────────────
const loadSchedule = async () => {
  loading.value = true
  try {
    // Load all lessons (no date filter) so stats are always accurate
    const res = await api.get('/student/schedule')
    allLessons.value = res.data?.lessons || []
  } catch { /* ignore */ } finally {
    loading.value = false
  }
}

const loadCurrentInstructor = async () => {
  const instructorId = authStore.user?.instructorId
  if (!instructorId) return
  try {
    const res = await api.get(`/instructors/${instructorId}`)
    currentInstructor.value = res.data
  } catch { /* ignore */ }
}

// ─── Lifecycle ────────────────────────────────────────────────────────────────
onMounted(async () => {
  await Promise.all([loadCurrentInstructor(), loadSchedule()])
})
</script>

<style scoped>
.schedule-card {
  border-radius: 12px;
  overflow: hidden;
}
.min-w-0 { min-width: 0; }

/* ── Grid ── */
.sg-grid {
  display: grid;
  overflow-x: auto;
  font-size: 0.75rem;
}

.sg-cell {
  padding: 4px 3px;
  border-right: 1px solid rgba(255, 255, 255, 0.06);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 36px;
  user-select: none;
}

/* Corner */
.sg-corner {
  background: rgba(255, 255, 255, 0.04);
  font-size: 0.60rem;
  color: rgba(255, 255, 255, 0.4);
  font-weight: 600;
  text-align: center;
  line-height: 1.2;
  padding: 2px 4px;
}

/* Column headers (dates) */
.sg-col-header {
  flex-direction: column;
  gap: 1px;
  background: rgba(255, 255, 255, 0.04);
  font-weight: 600;
  cursor: default;
  min-width: 44px;
}
.sg-col-header.sg-today-col {
  background: rgba(var(--v-theme-primary), 0.18);
  border-bottom: 2px solid rgb(var(--v-theme-primary));
}
.sg-date-num   { font-size: 0.88rem; font-weight: 700; line-height: 1; }
.sg-date-month { font-size: 0.60rem; color: rgba(255, 255, 255, 0.55); line-height: 1; }
.sg-date-dow   { font-size: 0.58rem; color: rgba(255, 255, 255, 0.4);  line-height: 1; }

/* Row headers (hours) */
.sg-row-header {
  background: rgba(255, 255, 255, 0.04);
  font-weight: 600;
  font-size: 0.70rem;
  color: rgba(255, 255, 255, 0.65);
  flex-shrink: 0;
}

/* Data cells */
.sg-data-cell {
  cursor: pointer;
  transition: background 0.12s ease;
  min-height: 36px;
  position: relative;
}

.sg-dot {
  pointer-events: none;
}

/* ── Cell states ── */
.sg-data-cell.cell-empty            { background: rgba(255,255,255,0.02); }
.sg-data-cell.cell-empty:hover      { background: rgba(255,255,255,0.07); }

.sg-data-cell.cell-today-empty      { background: rgba(var(--v-theme-primary), 0.04); }
.sg-data-cell.cell-today-empty:hover{ background: rgba(var(--v-theme-primary), 0.12); }

/* SCHEDULED */
.sg-data-cell.cell-scheduled        { background: rgba(33, 150, 243, 0.26); }
.sg-data-cell.cell-scheduled:hover  { background: rgba(33, 150, 243, 0.42); }
.sg-data-cell.cell-today-scheduled  { background: rgba(var(--v-theme-primary), 0.36); }
.sg-data-cell.cell-today-scheduled:hover { background: rgba(var(--v-theme-primary), 0.52); }

/* COMPLETED */
.sg-data-cell.cell-completed        { background: rgba(76, 175, 80, 0.22); }
.sg-data-cell.cell-completed:hover  { background: rgba(76, 175, 80, 0.36); }
.sg-data-cell.cell-today-completed  { background: rgba(76, 175, 80, 0.32); }

/* RESCHEDULED */
.sg-data-cell.cell-rescheduled      { background: rgba(255, 152, 0, 0.22); }
.sg-data-cell.cell-rescheduled:hover{ background: rgba(255, 152, 0, 0.36); }
.sg-data-cell.cell-today-rescheduled{ background: rgba(255, 152, 0, 0.32); }

/* ── Details dialog ── */
.detail-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
.detail-row:last-child { border-bottom: none; }
.detail-icon { margin-top: 2px; flex-shrink: 0; }
</style>
